<script setup>
import { reactive } from 'vue';

// // Module imports
import PollBranding from './PollBranding.vue';
import { PollSimpleOptions } from './pollSimpleUtils';

// // Component imports
import FluxButton from '@/components/FluxButton/FluxButtonComp.vue';

// // Asset imports
// import overlayDividerHorizontalSrc from '@/assets/images/overlays/divider-horizontal-arrows.png';

const props = defineProps({
  inputBoxBackground: {
    type: Object,
    // required: true,
    default: reactive({
      background: 'darkmagenta',
    }),
  },
  isInteractionMobile: {
    type: Boolean,
    default: false,
  },
  hasPollHeader: {
    type: Boolean,
    default: false,
  },
  hasDismissal: {
    type: Boolean,
    default: false,
  },
  hasProgress: {
    type: Boolean,
    default: false,
  },
  hasProgressBegun: {
    type: Boolean,
    default: false,
  },
  hasOrbs: {
    type: Boolean,
    default: false,
  },
  isFocusOnDismiss: {
    type: Boolean,
    default: false,
  },
  adVoteState: {
    type: String,
    default: '',
  },
  adHeadline: {
    type: String,
    default: '',
  },
  adHeadlineColor: {
    type: String,
    default: 'rgba(11, 11, 11, 1)',
  },
  adTagline: {
    type: String,
    default: '',
  },
  adTaglineColor: {
    type: String,
    default: 'rgba(11, 11, 11, 1)',
  },
  preBrandCopy: {
    type: String,
    // required: true,
    default: 'Sponsored by',
  },
  pollPlacement: {
    type: String,
    // required: true,
    default: '',
  },
  pollOptions: {
    type: Array,
    // required: true,
    default: () => [
      {
        optionEntry: 'One',
        optionLabel: PollSimpleOptions.OPTION1,
        optionIndex: 0,
        optionPosition: 1,
      },
      {
        optionEntry: 'Two',
        optionLabel: PollSimpleOptions.OPTION2,
        optionIndex: 1,
        optionPosition: 2,
      },
      {
        optionEntry: 'Three',
        optionLabel: PollSimpleOptions.OPTION3,
        optionIndex: 2,
        optionPosition: 3,
      },
    ],
  },
  focusIndex: {
    type: Number,
    // required: true,
    default: 0,
  },
  brandLogos: {
    type: Array,
    default: () => [],
  },
  breakUuid: {
    type: String,
    // required: true,
    default: '',
  },
  progressDuration: {
    type: Number,
    default: 15000,
  },
});

const emit = defineEmits(['pollInputEmitted', 'pollReadinessEmitted']);

const styleObjectHeadline = reactive({
  color: props.adHeadlineColor,
});

const styleObjectTagline = reactive({
  color: props.adTaglineColor,
});

// // PollInput functions

const handlePoke = (indexParam) => {
  if (props.isInteractionMobile) {
    emit('pollInputEmitted', indexParam);
  }
};

// const handleButtonCopyComparison = (defaultCopyParam, pollOptionParam) => {
//   if (defaultCopyParam !== pollOptionParam) {
//     emit('pollReadinessEmitted', true);
//   }
// };
const handleButtonCopyComparison = () => {
  emit('pollReadinessEmitted', true);
};
</script>

<template>
  <div
    class="poll-input"
    :class="{
      'poll-input--open': props.hasProgress && props.hasProgressBegun,
      'is-interaction-mobile': props.isInteractionMobile,
    }"
  >
    <div class="poll-input__container">
      <div
        v-if="props.hasOrbs && props.hasProgress && props.hasProgressBegun"
        class="orb io1"
      ></div>

      <div
        v-if="props.hasOrbs && props.hasProgress && props.hasProgressBegun"
        class="orb io2"
      ></div>

      <div
        v-if="props.hasOrbs && props.hasProgress && props.hasProgressBegun"
        class="orb io3"
      ></div>

      <div
        v-if="props.hasOrbs && props.hasProgress && props.hasProgressBegun"
        class="orb io4"
      ></div>

      <div
        v-if="props.hasOrbs && props.hasProgress && props.hasProgressBegun"
        class="orb io5"
      ></div>

      <!-- <div class="poll-input__container-wrapper-branding"> -->
      <div
        class="poll-input__wrapper"
        :class="{
          'poll-input__wrapper--upper-left': props.pollPlacement === 'upper_left',
          'poll-input__wrapper--upper-right': props.pollPlacement === 'upper_right',
          'poll-input__wrapper--lower-left': props.pollPlacement === 'lower_left',
          'poll-input__wrapper--lower-right': props.pollPlacement === 'lower_right',
        }"
      >
        <div class="poll-input__backdrop" :style="inputBoxBackground"></div>

        <div class="poll-input__contents">
          <div v-if="props.hasPollHeader" class="poll-input__header">
            <span class="poll-input__header-copy">Poll</span>
          </div>

          <div class="poll-input__vote-state">
            <span class="poll-input__vote-state-copy">{{ props.adVoteState }}</span>
          </div>

          <div
            v-if="props.adHeadline"
            class="poll-input__tagline in-headline"
            :style="styleObjectHeadline"
          >
            <span class="poll-input__tagline-copy">{{ props.adHeadline }}</span>
          </div>
          <div
            v-else-if="props.adTagline && !props.adHeadline"
            class="poll-input__tagline in-tagline"
            :style="styleObjectTagline"
          >
            <span class="poll-input__tagline-copy">{{ props.adTagline }}</span>
          </div>

          <!-- <div class="poll-input__separator" :style="styleObjectTagline">
              <img class="poll-input__divider" :src="overlayDividerHorizontalSrc" />
            </div> -->

          <div
            class="poll-input__button-row"
            :class="{
              'has-dismissal': props.hasDismissal,
            }"
            :style="styleObjectTagline"
          >
            <div
              v-for="item in props.pollOptions"
              :key="item.optionPosition"
              class="poll-input__option-button"
            >
              <FluxButton
                :class="{ active: item.optionIndex === props.focusIndex }"
                :button-copy="item.optionEntry"
                @click="handlePoke(item.optionIndex)"
                @copy-check-emitted="handleButtonCopyComparison($event, item.optionEntry)"
              />
            </div>
          </div>
        </div>

        <div
          v-if="props.hasDismissal"
          class="poll-input__dismissal"
          :class="{ active: props.isFocusOnDismiss }"
        >
          <span class="poll-input__dismissal-copy">Dismiss</span>
        </div>

        <div v-if="props.hasProgress && props.hasProgressBegun" class="poll-input__progress">
          <div
            class="poll-input__progress-bar"
            :class="{
              'poll-input__progress-bar--closed': !hasProgressBegun,
              'poll-input__progress-bar--open': hasProgressBegun,
            }"
            :style="{ '--progress-duration': `${props.progressDuration / 1000}s` }"
          ></div>
        </div>

        <PollBranding
          v-if="
            props.brandLogos &&
            props.brandLogos.length > 0 &&
            props.brandLogos[0] &&
            props.brandLogos[0].sponsorLogoImageSrc
          "
          :poll-pre-brand-copy="props.preBrandCopy"
          :poll-pre-brand-color="styleObjectTagline"
          :poll-brand-logos="props.brandLogos"
        />
      </div>
    </div>
  </div>
</template>
